import { User } from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { CgSpinner } from 'react-icons/cg';
import {
  FaCopy,
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaFolder,
  FaFolderOpen,
  FaPencil,
} from 'react-icons/fa6';
import { PiWarningFill } from 'react-icons/pi';
import { useThrottledCallback } from 'use-debounce';
import Conditional from '../../../shared/conditional/conditional';
import useToast from '../../../shared/hooks/toasts';
import If from '../../../shared/if/if';
import InboxItem from '../../../ui/inbox-table/inbox-item/inbox-item';
import { Tooltip } from '../../../ui/tooltip/tooltip';

export type DeviceGroupInboxItemProps = {
  title: string;
  deviceCount: number;
  creator?: User;
  danger?: boolean;
  active: boolean;
  visible?: boolean;
  groupCode?: string;
  onClick: () => void;
  onVisibilityClick?: () => void;
  onEditGroupClick?: () => void;
  onExportDeviceGroupDevicesClick?: () => void;
  disableActions?: boolean;
  showCreator?: boolean;
};

export function DeviceGroupInboxItem({
  title,
  deviceCount,
  creator,
  active,
  visible = true,
  danger = false,
  groupCode,
  onClick,
  onVisibilityClick,
  onEditGroupClick,
  onExportDeviceGroupDevicesClick,
  disableActions = false,
  showCreator = true,
}: DeviceGroupInboxItemProps) {
  const toast = useToast();
  const deviceGroupTitle = useMemo(() => {
    const devicesText = deviceCount === 1 ? 'device' : 'devices';
    return `${title} - ${deviceCount} ${devicesText}`;
  }, [title, deviceCount]);
  const [isExportingData, setIsExportingData] = useState(false);

  const deviceGroupCreatorText = useMemo(() => {
    // If no creator is provided, the device group was created during the work order flow
    let creatorText = 'zSpace';
    if (creator) {
      creatorText = creator.deletedAt ? 'deleted user' : creator.email;
    }
    return `Created by ${creatorText}`;
  }, [creator]);

  const copyGroupCode = useThrottledCallback(() => {
    navigator.clipboard.writeText(groupCode!);
    toast.success('Group code copied to clipboard');
  }, 3000);

  const handleCopyGroupCode = useCallback(
    (e: React.MouseEvent) => {
      if (!groupCode) return;

      e.stopPropagation();
      copyGroupCode();
    },
    [copyGroupCode, groupCode]
  );

  const handleVisibilityClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onVisibilityClick?.();
    },
    [onVisibilityClick]
  );

  const handleEditGroupClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      onEditGroupClick?.();
    },
    [onEditGroupClick]
  );

  const handleDownloadGroupDevicesDataClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      setIsExportingData(true);
      try {
        await onExportDeviceGroupDevicesClick?.();
      } finally {
        setIsExportingData(false);
      }
    },
    [onExportDeviceGroupDevicesClick]
  );

  return (
    <InboxItem active={active} onClick={onClick}>
      <Conditional condition={active}>
        <Conditional.True>
          <Element display="flex" flexDirection="column" className="gap-1">
            <Element>
              <Icon color="white">
                <FaFolderOpen />
              </Icon>
              <span>{deviceGroupTitle}</span>
              <If condition={!disableActions}>
                <Icon
                  color="white"
                  className="is-clickable"
                  onClick={() => onVisibilityClick?.()}
                  pl={2}
                >
                  <Conditional condition={visible}>
                    <Conditional.True>
                      <Tooltip message="Hide">
                        <FaEyeSlash />
                      </Tooltip>
                    </Conditional.True>
                    <Conditional.False>
                      <Tooltip message="Show">
                        <FaEye />
                      </Tooltip>
                    </Conditional.False>
                  </Conditional>
                </Icon>
                <Icon
                  color="white"
                  className="is-clickable"
                  onClick={handleEditGroupClick}
                  pl={2}
                >
                  <Tooltip message="Edit">
                    <FaPencil />
                  </Tooltip>
                </Icon>
                <Icon
                  color="white"
                  className="is-clickable"
                  onClick={handleDownloadGroupDevicesDataClick}
                  pl={2}
                >
                  <Conditional condition={isExportingData}>
                    <Conditional.True>
                      <CgSpinner className="animate-spin" />
                    </Conditional.True>
                    <Conditional.False>
                      <Tooltip message="Export devices data">
                        <FaDownload />
                      </Tooltip>
                    </Conditional.False>
                  </Conditional>
                </Icon>
              </If>
            </Element>
            <If condition={showCreator}>
              <Element pl={1}>
                <span>{deviceGroupCreatorText}</span>
              </Element>
            </If>
            <If condition={!!groupCode}>
              <Element pl={1}>
                <span>Group code {groupCode}</span>
                <Tooltip message="Copy code">
                  <Icon
                    className="is-clickable"
                    color="white"
                    onClick={handleCopyGroupCode}
                  >
                    <FaCopy />
                  </Icon>
                </Tooltip>
              </Element>
            </If>
          </Element>
        </Conditional.True>
        <Conditional.False>
          <Icon color={visible ? 'primary-dark' : 'grey-light'}>
            <FaFolder />
          </Icon>
          <span className={visible ? '' : 'has-text-grey-light'}>
            {deviceGroupTitle}
          </span>
          <If condition={!disableActions && !visible}>
            <Icon
              color="grey-light"
              className="is-clickable"
              onClick={handleVisibilityClick}
            >
              <FaEye />
            </Icon>
          </If>
          <If condition={danger}>
            <Icon color="danger-dark">
              <PiWarningFill />
            </Icon>
          </If>
        </Conditional.False>
      </Conditional>
    </InboxItem>
  );
}

export default DeviceGroupInboxItem;
